import React, {useCallback, useContext, useState} from 'react';
import {useDispatch} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import clsx from 'clsx';
import {CopyButton} from 'src/components/copy-button';
import {Skeleton} from 'src/components/skeleton';
import {ACCOUNT_TABS, CONTRACT_ID_EXPLANATION, SAVE_CHANGES_SUCCESS_MESSAGE} from 'src/constants';
import {MessageContext} from 'src/contexts/message-context';
import {useAdminAccessRight} from 'src/hooks/access-rights';
import {useDateTime} from 'src/hooks/date-time/useDateTime';
import {useLicenseTypes} from 'src/hooks/dictionaries';
import {useEventTracking} from 'src/hooks/useEventTracking';
import {SEATS_RANGE_LICENSES} from 'src/modules/profile/account/constants';
import {
    clearAccountLicense as clearAccountProfileLicense,
} from 'src/modules/profile/account/services/account-page-slice';
import {
    clearAccountLicense,
    clearAccountLicenseHistory,
} from 'src/modules/profile/account/tabs/license-and-features/services/account-license-features-slice';
import {formatLicenseDate} from 'src/modules/profile/utils/formatLicenseDate';
import {LicenseType} from 'src/openapi/admin/models/LicenseType';
import {getSeatsRangeTitle, isLicenseTypeProhibitedForCreation} from 'src/utils/license-util';

import Button, {FlatButton} from '@pb/kit-component-button';
import {Dialog} from '@pb/kit-component-dialog-v2';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {FormSection} from '@pb/kit-component-form-section';
import {Integer} from '@pb/kit-component-formatters';
import {Group, Item} from '@pb/kit-component-group';
import {Sizes} from '@pb/kit-component-icon';
import {Info} from '@pb/kit-component-info';
import {LocaleContext} from '@pb/kit-context-locale';

import {LicenseCreatorLink} from '../../components/license-creator-link';

import {EditLicenseInformationModal} from './edit-license-information-modal';
import {LicenseInformationSkeleton} from './license-information-skeleton';
import {useLicenseInformation} from './useLicenseInformation';

import {
    licenseInformationHeadline,
    licenseInformationLicenseState,
    licenseInformationLicenseStateActive,
} from './license-information.module.styl';

import type {MessageContextType} from 'src/contexts/message-context';

type Props = {
    licenseId: number,
    accountId: number,
};

export const LicenseInformation: React.FC<Props> = ({licenseId, accountId}) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {isFutureDate} = useDateTime();

    const {locale} = useContext(LocaleContext);
    const {setErrorMessage, setSuccessMessage} = useContext<MessageContextType>(MessageContext);

    const {getLicenseTypeByCode} = useLicenseTypes();
    const [shownEditModal, setShownEditModal] = useState(false);
    const {licenseCreateOpen} = useEventTracking();
    const {canCreateLicense, canEditLicense, canCreateLicenseFromExisted} = useAdminAccessRight();

    const [saveBusy, setSaveBusy] = useState(false);

    const {
        licenseInformationLoading,
        licenseInformation,
        initialLicenseInformation: license,
        setLicenseInformation,
        isExpiredLicense,
        isInitialLicenseInformation,
        onSaveLicenseInformation,
        onDiscard,
    } = useLicenseInformation({licenseId});

    const onSave = useCallback(async () => {
        const calls: (string | null)[] = [];
        setSaveBusy(true);
        if (!isInitialLicenseInformation) {
            calls.push(await onSaveLicenseInformation());
        }
        setSaveBusy(false);

        const errors = [...new Set(calls.filter(Boolean))];
        setShownEditModal(false);

        if (errors.length) {
            setErrorMessage(errors.join(' '));
        } else {
            setSuccessMessage(SAVE_CHANGES_SUCCESS_MESSAGE);
            dispatch(clearAccountLicense());
            dispatch(clearAccountProfileLicense());
            dispatch(clearAccountLicenseHistory());
        }
    }, [
        onSaveLicenseInformation,
        isInitialLicenseInformation,
        setErrorMessage,
        setSuccessMessage,
        dispatch,
    ]);

    const onClose = () => {
        onDiscard();
        setShownEditModal(false);
    };

    const navigateToNewFromExisting = useCallback(() => {
        licenseCreateOpen({fromExisting: true});
        navigate(`/profile/account/${accountId}/${ACCOUNT_TABS.CREATE_LICENSE.name}/${licenseId}`);
    }, [accountId, licenseId, licenseCreateOpen, navigate]);

    const navigateToLicense = useCallback(() => {
        licenseCreateOpen({fromExisting: false});
        navigate(`/profile/account/${accountId}/${ACCOUNT_TABS.CREATE_LICENSE.name}`);
    }, [accountId, licenseCreateOpen, navigate]);
    const isCreateFromExistingDisabled = isLicenseTypeProhibitedForCreation(license?.typeCode as LicenseType);

    const licenseStateClasses = clsx(licenseInformationLicenseState, [
        license?.isActive && licenseInformationLicenseStateActive,
    ]);

    return (
        <Skeleton view={<LicenseInformationSkeleton/>} shown={licenseInformationLoading}>
            <FormSection
                headline="License"
                info={headlineText => (
                    <Item grow={1}>
                        <Group justify="space-between" alignItems="start">
                            <Group gap="none">
                                {headlineText}
                                <div className={licenseInformationHeadline}>
                                    ID &nbsp;
                                    {license?.licenseId.toString()}
                                    <CopyButton
                                        copiedText={license?.licenseId.toString() ?? ''}
                                        entityName="License ID"
                                        iconSize={Sizes.Size_16}
                                    /> | &nbsp;
                                </div>
                                <strong className={licenseStateClasses}>
                                    {license?.isActive ? 'Active' : null}
                                    {!license?.isActive && !isFutureDate(license?.startDate) && 'Expired'}
                                    {!license?.isActive && isFutureDate(license?.startDate) && 'Pending'}
                                </strong>
                            </Group>
                            {canEditLicense(accountId) ? (
                                <FlatButton disabled={isExpiredLicense} onClick={() => setShownEditModal(true)}>
                                    <b>Edit</b>
                                </FlatButton>
                            ) : null}
                        </Group>
                    </Item>
                )}>
                <Group direction="v" alignItems="start">
                    <Group gap="none">
                        <strong style={{width: 100}}>License period</strong>
                        <span>{formatLicenseDate(
                            locale.dateFormat,
                            license?.startDate,
                        )} to {formatLicenseDate(
                            locale.dateFormat,
                            license?.endDate,
                        )}
                        </span>
                    </Group>
                    <Group>
                        <Group style={{width: 250}} gap="none">
                            <strong style={{width: 100}}>License type</strong>
                            <span>{license ? getLicenseTypeByCode(license.typeCode) : '--'}</span>
                        </Group>
                        <Group gap="none">
                            <strong style={{width: 80}}>Seats</strong>
                            {SEATS_RANGE_LICENSES.includes(license?.typeCode as LicenseType)
                                ? (
                                        <span>{getSeatsRangeTitle(license?.concurrentUserCount).title}</span>
                                    )
                                : (
                                        <span>{license?.concurrentUserCount
                                            ? Integer.format(license.concurrentUserCount, locale, {showZeros: true})
                                            : 'Unlimited'}
                                        </span>
                                    )}
                        </Group>
                    </Group>
                    <Group>
                        <Group style={{width: 250}} gap="none">
                            <strong style={{width: 100}}>Created by</strong>
                            <div style={{width: 150}}>
                                {license ? <LicenseCreatorLink license={license}/> : '--'}
                            </div>
                        </Group>
                        <Group gap="none">
                            <strong style={{width: 80}}>Created on</strong>
                            {license ? formatLicenseDate(locale.dateFormat, license.createdOn) : '--'}
                        </Group>
                    </Group>
                    <Group gap="none">
                        <Item style={{width: 100}}>
                            <Info type={{popover: CONTRACT_ID_EXPLANATION}}>
                                <strong>Contract ID</strong>
                            </Info>
                        </Item>
                        <Item style={{width: 300}}>
                            <EllipsisTextHint title={license?.contractId || '--'}>
                                {license?.contractId || '--'}
                            </EllipsisTextHint>
                        </Item>
                    </Group>
                </Group>
                <Group className="pt-l">
                    {canCreateLicense(accountId) ? (
                        <Button variant="Type.PRIMARY" onClick={navigateToLicense}>
                            New License
                        </Button>
                    ) : null}
                    {canCreateLicenseFromExisted(accountId) ? (
                        <Button
                            variant="Type.SECONDARY"
                            disabled={isCreateFromExistingDisabled}
                            onClick={navigateToNewFromExisting}>
                            New License from Existing
                        </Button>
                    ) : null}
                </Group>
            </FormSection>
            <Dialog
                shown={shownEditModal}
                setShown={onClose}
                wrapperProps={{style: {width: 350}}}>
                {licenseInformation ? (
                    <EditLicenseInformationModal
                        busy={saveBusy}
                        license={licenseInformation}
                        onChange={setLicenseInformation}
                        isInitialLicenseInformation={isInitialLicenseInformation}
                        onSave={onSave}
                        onClose={onClose}
                    />
                ) : null}
            </Dialog>
        </Skeleton>
    );
};
